import { Fragment, useState, useEffect, useCallback, useRef } from "react";
import queryString from "query-string";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import makeStyles from "styles/muiStylesCompat/makeStyles";
import { useNavigate, useLocation } from "react-router-dom";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ToggleButton from "@mui/material/ToggleButton";
import ViewContainer from "components/ViewContainer/ViewContainer";
import { driverOptionalPhoneSchema } from "~/validation/contactFields";
import ButtonPrimary from "components/Buttons/ButtonPrimary";
import { parseRegistrationApiErrors } from "~/utils/registrationErrors";

import DriverForm from "components/DriverForm/DriverForm";
import { httpAcceptInviteToken } from "services/http/companies";

import AuthService from "services/http/AuthService";
const auth = new AuthService();

const styles = (theme) => ({
  fieldGrid: {
    marginBottom: 12,
  },
  toggleButton: {},
  toggleButtonSelected: {
    color: "#fff" + " !important",
    backgroundColor: theme.palette.primary.main + " !important",
  },
});

const useStyles = makeStyles(styles);

const RegisterUser = (props) => {
  const classes = useStyles();

  const [values, setValues] = useState({
    username: "",
    password: "",
  });

  const [invitationToken, setInvitationToken] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState(null);
  const [terms, setTerms] = useState(false);

  const [registerState, setRegisterState] = useState(0);
  const errorAlertRef = useRef(null);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = props;
  const { search } = location;

  const handleAcceptInviteToken = useCallback(
    (companyId, token) => {
      setSaving(true);
      httpAcceptInviteToken(companyId, token).then(
        (response) => {
          setValues((prev) => ({ ...prev, ...response }));
          setSaving(false);
        },
        (_error) => {
          setSaving(false);
          enqueueSnackbar(
            t(
              "Kutsun tietojen lataus epäonnistui. Tarkista linkki tai ota yhteyttä tukeen.",
            ),
            { variant: "error" },
          );
        },
      );
    },
    [enqueueSnackbar, t],
  );

  useEffect(() => {
    if (search) {
      const parsed = queryString.parse(search);

      if (parsed.token && parsed.token.length < 64) {
        enqueueSnackbar(
          t(
            "Kutsulinkki on virheellinen. Tarkista, että sähköpostissa saatu linkki on täydellinen.",
          ),
          { variant: "error" },
        );
        navigate("/");
      }

      if (parsed.token && parsed.company) {
        setInvitationToken(parsed.token);
        handleAcceptInviteToken(parsed.company, parsed.token);
      }
    }
  }, [search, handleAcceptInviteToken, enqueueSnackbar, navigate, t]);

  const handleTerms = (event, newTerms) => {
    if (newTerms == null) return;
    setTerms(newTerms);
    if (newTerms === true) {
      setErrors((prev) => {
        if (!prev?.terms) return prev;
        const { terms: _terms, ...rest } = prev;
        return Object.keys(rest).length ? rest : null;
      });
    }
  };

  const handleInputChange = (event) => {
    let { value, name } = event.target;

    if (["email", "username"].indexOf(name) > -1) {
      value = value.toLowerCase();
    }

    setErrors((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      delete next[name];
      return Object.keys(next).length ? next : null;
    });

    setValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const onSubmit = (event) => {
    event.preventDefault();

    if (terms !== true) {
      setErrors((prev) => ({
        ...(prev || {}),
        terms: t("Hyväksy käyttöehdot jatkaaksesi"),
        inValid: true,
      }));
      return;
    }

    setSaving(true);
    setErrors(null);

    validateForm().then(
      () => {
        handleRegister();
      },
      () => {
        setSaving(false);
      },
    );
  };

  const handleRegister = () => {
    setSaving(true);
    const request = {
      data: {
        ...values,
        username: values.email.toLowerCase(),
        email: values.email.toLowerCase(),
        accept_terms: true,
        name: values.first_name + " " + values.last_name,
        first_name: values.first_name,
        last_name: values.last_name,
        password: values.password1,
      },
      params: {
        token: invitationToken,
      },
    };
    auth.register(request).then(
      (_response) => {
        auth.login(values.email.toLowerCase(), values.password1).then(
          (_response) => {
            setRegisterState(1);
            setSaving(false);
          },
          (_loginError) => {
            setRegisterState(1);
            setSaving(false);
            enqueueSnackbar(
              t(
                "Tili luotiin. Jos et pääse automaattisesti sisään, kirjaudu manuaalisesti.",
              ),
              { variant: "warning" },
            );
          },
        );
      },
      (errors) => {
        handleErrors(errors);
        setSaving(false);
      },
    );
  };

  const handleErrors = (apiError) => {
    const { fieldErrors, message } = parseRegistrationApiErrors(apiError, t);
    setErrors({
      ...fieldErrors,
      message,
      inValid: true,
    });
  };

  const updateValues = (updated_values) => {
    if (updated_values == null) {
      return;
    }

    setValues((prev) => ({ ...prev, ...updated_values }));
  };

  const goBack = () => {
    setRegisterState(0);
  };

  const goDashboard = () => {
    navigate("/dashboard/start");
  };

  const validationSchema = yup.object().shape({
    first_name: yup.string().required(t("Pakollinen kenttä")),
    last_name: yup.string().required(t("Pakollinen kenttä")),
    email: yup
      .string()
      .email(t("Sähköpostiosoite ei ole kelvollinen"))
      .required(t("Pakollinen kenttä")),
    password2: yup
      .string()
      .required(t("Pakollinen kenttä"))
      .min(8, t("Salasanan on oltava vähintään 8 merkkiä"))
      .oneOf(
        [yup.ref("password1"), null],
        t("Salasanan on täsmättävä uusi salasana kentän kanssa"),
      ),
    password1: yup
      .string()
      .required(t("Pakollinen kenttä"))
      .min(8, t("Salasanan on oltava vähintään 8 merkkiä")),
    phone: driverOptionalPhoneSchema(),
  });

  const validateForm = () => {
    return new Promise((resolve, reject) => {
      validationSchema
        .validate(values, { abortEarly: false })
        .then(() => {
          resolve(true);
        })
        .catch((err) => {
          const fieldErrors = { inValid: true };
          if (err?.inner?.length) {
            err.inner.forEach((validationError) => {
              if (validationError.path) {
                fieldErrors[validationError.path] = t(validationError.message);
              }
            });
          } else if (err?.path) {
            fieldErrors[err.path] = t(err.message);
          }
          setErrors(fieldErrors);
          reject("NO_GOOD");
        });
    });
  };

  const errorSummary =
    errors?.message ||
    errors?.terms ||
    (errors?.inValid
      ? t("Tarkista korostetut kentät ja yritä uudelleen.")
      : null);

  useEffect(() => {
    if (errorSummary) {
      errorAlertRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [errorSummary]);

  return (
    <Fragment>
      {registerState == 0 ? (
        <ViewContainer title={t("Rekisteröidy")}>
          <form onSubmit={onSubmit} noValidate>
            <Grid container className={classes.container}>
              <Grid className={classes.fieldGrid} size={{ xs: 12 }}>
                <Typography variant="subtitle1">{t("Etunimi")}</Typography>
                <TextField
                  fullWidth
                  id="register-first_name"
                  name="first_name"
                  value={values.first_name}
                  onChange={handleInputChange}
                  margin="dense"
                  variant="outlined"
                  required
                  error={Boolean(errors?.first_name)}
                  helperText={errors?.first_name}
                />
              </Grid>

              <Grid className={classes.fieldGrid} size={{ xs: 12 }}>
                <Typography variant="subtitle1">{t("Sukunimi")}</Typography>
                <TextField
                  fullWidth
                  id="register-last_name"
                  name="last_name"
                  value={values.last_name}
                  onChange={handleInputChange}
                  margin="dense"
                  variant="outlined"
                  required
                  error={Boolean(errors?.last_name)}
                  helperText={errors?.last_name}
                />
              </Grid>

              <Grid className={classes.fieldGrid} size={{ xs: 12 }}>
                <Typography variant="subtitle1">
                  {t("Sähköposti / käyttäjätunnus")}
                </Typography>
                <TextField
                  fullWidth
                  id="register-email"
                  name="email"
                  value={values.email}
                  onChange={handleInputChange}
                  type="email"
                  margin="dense"
                  variant="outlined"
                  required
                  error={Boolean(errors?.email)}
                  helperText={errors?.email}
                />
              </Grid>

              <Grid className={classes.fieldGrid} size={{ xs: 12 }}>
                <Typography variant="subtitle1">{t("Salasana")}</Typography>
                <TextField
                  fullWidth
                  id="register-password1"
                  name="password1"
                  type="password"
                  value={values.password1}
                  onChange={handleInputChange}
                  margin="dense"
                  variant="outlined"
                  required
                  error={Boolean(errors?.password1)}
                  helperText={errors?.password1}
                />
              </Grid>

              <Grid className={classes.fieldGrid} size={{ xs: 12 }}>
                <Typography variant="subtitle1">
                  {t("Salasana uudelleen")}
                </Typography>

                <TextField
                  fullWidth
                  id="register-password2"
                  name="password2"
                  type="password"
                  value={values.password2}
                  onChange={handleInputChange}
                  margin="dense"
                  variant="outlined"
                  required
                  error={Boolean(errors?.password2)}
                  helperText={errors?.password2}
                />
              </Grid>

              <Box sx={{ mb: "16px" }}>
                <DriverForm
                  values={values}
                  updateValues={updateValues}
                  fields={["phone"]}
                />
                {errors?.phone ? (
                  <Typography variant="caption" color="error" display="block">
                    {errors.phone}
                  </Typography>
                ) : null}
              </Box>

              <Grid size={{ xs: 12 }}>
                <Box sx={{ mb: "16px" }}>
                  <Grid
                    container
                    direction="row"
                    sx={{
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <Grid size={12}>
                      <a href="https://opentaxi.fi/legal" target="_blank">
                        <Typography variant="subtitle1">
                          {t("Käyttöehdot")}
                        </Typography>
                      </a>
                    </Grid>

                    <Grid size={12}>
                      <ToggleButtonGroup
                        value={terms}
                        exclusive
                        onChange={handleTerms}
                        aria-label="text alignment"
                      >
                        <ToggleButton
                          classes={{ selected: classes.toggleButtonSelected }}
                          className={classes.toggleButton}
                          value={true}
                          aria-label="left aligned"
                        >
                          {t("Hyväksyn")}
                        </ToggleButton>
                        <ToggleButton
                          classes={{ selected: classes.toggleButtonSelected }}
                          className={classes.toggleButton}
                          value={false}
                          aria-label="right aligned"
                        >
                          {t("En")}
                        </ToggleButton>
                      </ToggleButtonGroup>
                      {errors?.terms ? (
                        <Typography
                          variant="caption"
                          color="error"
                          display="block"
                          sx={{ mt: 1 }}
                        >
                          {errors.terms}
                        </Typography>
                      ) : terms !== true ? (
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          display="block"
                          sx={{ mt: 1 }}
                        >
                          {t("Hyväksy käyttöehdot jatkaaksesi")}
                        </Typography>
                      ) : null}
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Grid
                  container
                  direction="row"
                  sx={{ justifyContent: "space-between", alignItems: "flex-end" }}
                >
                  <Grid size={12}>
                    <Typography variant="subtitle2">
                      {t("Haluatko palvelun käyttöön?")}
                      <a
                        href="https://www.opentaxi.fi/laheta-yrityksesi-tiedot/"
                        target="_blank"
                      >
                        <Typography variant="subtitle2">
                          {t("Ota yhteyttä")}
                        </Typography>
                      </a>
                    </Typography>
                  </Grid>
                  <Grid size={12}>
                    {errorSummary ? (
                      <Box ref={errorAlertRef} sx={{ mt: 2, mb: 1 }}>
                        <Alert severity="error">{errorSummary}</Alert>
                      </Box>
                    ) : null}
                    <ButtonPrimary
                      type="submit"
                      loading={saving}
                      disabled={saving}
                    >
                      {saving ? t("Rekisteröidään...") : t("Rekisteröidy")}
                    </ButtonPrimary>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </form>
        </ViewContainer>
      ) : null}

      {registerState == 1 ? (
        <ViewContainer title={t("Olet rekisteröitynyt onnistuneesti!")}>
          <Stack
            direction="column"
            spacing={2}
            sx={{ alignItems: "center", width: 1 }}
          >
            <ButtonPrimary onClick={() => goDashboard()}>
              {t("Jatka sisään palveluun")}
            </ButtonPrimary>
          </Stack>
        </ViewContainer>
      ) : null}

      {registerState == 2 ? (
        <ViewContainer title={t("Rekisteröinti epäonnistui!")} type="error">
          <Stack
            direction="column"
            spacing={2}
            sx={{ alignItems: "center", width: 1 }}
          >
            <Typography variant="subtitle1">
              {t(
                "Palvelu on käytössä vain kutsutuille yrityksille. Ota yhteyttä, jos haluat palvelun käyttöön!",
              )}
            </Typography>

            <ButtonPrimary onClick={() => goBack()}>
              {t("Takaisin")}
            </ButtonPrimary>
          </Stack>
        </ViewContainer>
      ) : null}
    </Fragment>
  );
};

export default RegisterUser;
